// Types
import Alt from '@/types/models/alt';
import InvestmentItem from '@/types/investment-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { DETAILED_ASSET_ALLOCATION, PORTFOLIO_ASSET_ALLOCATION } from '@/enums/AssetAllocation';
import { getValidatedCountry } from './domicile';
import Dictionary from '@/types/dictionary';

const FULL_WEIGHT = 100;

/**
 * Creates an asset allocation object for alternative investments.
 * Sets all standard portfolio asset allocations to 0 and alternative investment to 100%.
 *
 * @returns {Dictionary<number>} An object containing asset allocations where alternative
 * investment is set to 100% and all other allocations are 0
 */
export const getAltAssetAllocations = () => {
  const defaultAllocations = Object.values(PORTFOLIO_ASSET_ALLOCATION).reduce((acc: Dictionary<number>, value) => {
    acc[value] = 0;
    return acc;
  }, {});

  return {
    ...defaultAllocations,
    [DETAILED_ASSET_ALLOCATION.ALTERNATIVE_INVESTMENT]: FULL_WEIGHT,
  };
};

/**
 * Converts an Alt type to an InvestmentItem type
 * @param alt The alternative investment to convert
 * @returns An InvestmentItem representation of the alt
 */
export const altToInvestmentItem = (alt: Alt): InvestmentItem => {
  const { domicile, _id } = alt;
  const country = getValidatedCountry(domicile);

  return {
    _id,
    allocations: {
      asset: [{ name: { 'en-CA': DETAILED_ASSET_ALLOCATION.ALTERNATIVE_INVESTMENT }, weight: FULL_WEIGHT }],
      country: [{ name: country, weight: FULL_WEIGHT }],
    },
  };
};

export const canViewAlts = (user?: Nullable<User>) => !!user && userHasPermissionEnabled(user, PERMISSIONS.PREQIN_ALT);
